import { Component, computed, effect, ElementRef, HostListener, inject, input, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import type { NotificationItem } from '../../core/types/notification.types';
import { AuthService } from '../../core/services/auth.service';
import { isUserRole } from '../../core/utils/admin-role';
import { NotificationsApiService } from '../../core/services/notifications-api.service';
import { PeopleSearchBarComponent } from '../../shared/people-search-bar/people-search-bar.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, PeopleSearchBarComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  pageTitle = input<string>('Minhas Listas');

  private readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  private readonly notificationsApi = inject(NotificationsApiService);

  /** Search, criar lista e notificações só para utilizadores com papel USER (não admin). */
  readonly showUserNavbarActions = computed(() => isUserRole(this.authService.currentUser()?.role));


  readonly notifRoot = viewChild<ElementRef<HTMLElement>>('notifRoot');

  readonly notificationsOpen = signal(false);
  readonly mobileSearchOpen = signal(false);
  readonly notificationsLoading = signal(false);
  readonly notificationsError = signal<string | null>(null);
  readonly notifications = signal<NotificationItem[]>([]);
  /** Contador de não lidas (atualizado ao abrir o painel e ao carregar a página). */
  readonly unreadCount = signal(0);

  constructor() {
    effect(() => {
      const session = this.authService.isAuthenticated();
      const userRole = this.authService.currentUser()?.role;
      if (session && isUserRole(userRole)) {
        this.refreshUnreadCount();
      } else {
        this.unreadCount.set(0);
        this.notificationsOpen.set(false);
        this.notifications.set([]);
        this.notificationsError.set(null);
        this.notificationsLoading.set(false);
      }
    });
  }

  private refreshUnreadCount(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.notificationsApi
      .list()
      .pipe(take(1), catchError(() => of([] as NotificationItem[])))
      .subscribe((list) => {
        this.unreadCount.set(list.filter((n) => !n.isRead).length);
      });
  }

  onCreateWishlist(): void {
    void this.router.navigate(['/wishlists'], { queryParams: { criarLista: '1' } });
  }

  openMobileSearch(ev: Event): void {
    ev.stopPropagation();
    this.mobileSearchOpen.set(true);
    this.notificationsOpen.set(false);
  }

  closeMobileSearch(): void {
    this.mobileSearchOpen.set(false);
  }

  toggleNotifications(ev: Event): void {
    ev.stopPropagation();
    if (this.notificationsOpen()) {
      this.notificationsOpen.set(false);
      return;
    }
    this.notificationsOpen.set(true);
    this.loadNotificationsPanel();
  }

  /**
   * Carrega a lista (GET), exibe no painel e marca todas como lidas (POST read-all),
   * sem precisar clicar em cada item.
   */
  private loadNotificationsPanel(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.notificationsLoading.set(true);
    this.notificationsError.set(null);
    this.notificationsApi
      .list()
      .pipe(
        take(1),
        switchMap((list) =>
          this.notificationsApi.markAllRead().pipe(
            map(() => list),
            catchError(() => of(list)),
          ),
        ),
        catchError(() => {
          this.notificationsError.set('Não foi possível carregar notificações.');
          return of([] as NotificationItem[]);
        }),
        finalize(() => this.notificationsLoading.set(false)),
      )
      .subscribe((list) => {
        this.notifications.set(list.map((n) => ({ ...n, isRead: true })));
        this.unreadCount.set(0);
      });
  }

  navigateToUser(username: string, ev: Event): void {
    ev.stopPropagation();
    this.notificationsOpen.set(false);
    void this.router.navigate(['/user', username, 'wishlists']);
  }

  formatNotificationDate(iso: string): string {
    if (!iso?.trim()) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    const t = event.target as Node;
    const notifRoot = this.notifRoot()?.nativeElement;
    if (this.notificationsOpen() && notifRoot && !notifRoot.contains(t)) {
      this.notificationsOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.mobileSearchOpen()) {
      this.mobileSearchOpen.set(false);
    }
  }
}
